/*
 * PRD-037 — Auth context PROPIO del portal del partner (app separada).
 * La sesion del partner persiste en sessionStorage bajo una llave DEDICADA (`partner_session`),
 * separada de la del panel: sobrevive una recarga (F5) pero se borra al cerrar la pestania
 * (menor exposicion que localStorage). El token de partner viaja en su header dedicado
 * `partner-token`, asi que esta separacion NO se cruza con el token de comercio. NO usa el
 * AuthContext del panel.
 */
import { createContext, useContext, useState, type ReactNode } from 'react';
import type { PartnerInfo } from '@services/partnerService';

interface PartnerAuthState {
  token: string | null;
  partner: PartnerInfo | null;
  login: (token: string, partner: PartnerInfo) => void;
  logout: () => void;
}

const STORAGE_KEY = 'partner_session';

interface PersistedSession {
  token: string;
  partner: PartnerInfo;
}

// Decodifica el `exp` del JWT (sin dependencias) para no restaurar una sesion ya vencida.
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return true; // token ilegible -> tratalo como invalido
  }
};

const readSession = (): PersistedSession | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed?.token || isTokenExpired(parsed.token)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const PartnerAuthCtx = createContext<PartnerAuthState>({
  token: null,
  partner: null,
  login: () => undefined,
  logout: () => undefined,
});

export const usePartnerAuth = () => useContext(PartnerAuthCtx);

export const PartnerAuthProvider = ({ children }: { children: ReactNode }) => {
  // Inicializador lazy: restaura la sesion (si existe y no expiro) una sola vez al montar.
  const [session, setSession] = useState<PersistedSession | null>(() => readSession());

  const login = (token: string, partner: PartnerInfo) => {
    const next = { token, partner };
    setSession(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* sessionStorage no disponible (modo privado estricto) -> sesion solo en memoria */
    }
  };

  const logout = () => {
    setSession(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* no-op */
    }
  };

  return (
    <PartnerAuthCtx.Provider
      value={{ token: session?.token ?? null, partner: session?.partner ?? null, login, logout }}
    >
      {children}
    </PartnerAuthCtx.Provider>
  );
};
